import {MANAGED_RECIPIENTS, RECIPIENT_TYPE_ID_KEYS} from '@constants/communicator';

import * as types from './types';

const INITIAL_STATE = {
  composeMessageInProgress: false,
  isSendingDraft: false,
  isSchedulingMessage: false,
  isCreatingDraft: false,
  personIds: [],
  numPersons: 0,
  allPersonsSelected: false,
  messageId: null,
  recipientIds: [],
  shouldFetchTemplateRecipients: false,
  fetchingRecipients: false,
  fetchingGroups: false,
  fetchingOptionalRecipients: false,
  fetchingOptionalGroups: false,
  fetchingSavedSearches: false,
  fetchingOptionalSavedSearches: false,
  sites: [],
  groups: [],
  savedSearches: [],
  recipients: [],
  optionalRecipients: [],
  optionalGroups: [],
  optionalSavedSearches: [],
  optionalSites: [],
  additionalRecipients: [],
  additionalGroups: [],
  additionalSavedSearches: [],
  additionalSites: [],
  organizationIds: null,
  pageable: {
    pageNumber: 0,
    pageSize: 25,
    totalElements: 0,
  },
  deletingRecipient: false,
  addingRecipient: false,
  messageTemplates: [],
  fromEmail: undefined,
  scheduledMessage: undefined,
  scheduledMessageLoading: false,
  isEditingAllowSend: false,
  deletedRecipients: [],
  addedRecipients: [],
  preserveRecipients: false,
  initialSubject: undefined,
  initialBody: undefined,
};

const communicatorReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case types.INITIATE_COMPOSE_MESSAGE:
      return {
        ...state,
        composeMessageInProgress: true,
        personIds: action.payload.personIds,
        numPersons: action.payload.numPersons,
        allPersonsSelected: action.payload.allPersonsSelected,
        isEditingAllowSend: action.payload.isEditingAllowSend,
        templateToSend: action.payload.templateToSend,
        scheduledMessage: undefined,
        preserveRecipients: action.payload.personIds?.length > 0 && !action.payload.templateToSend,
        initialSubject: action.payload.initialSubject,
        initialBody: action.payload.initialBody,
      };

    case types.INITIATE_COMPOSE_MESSAGE_ENHANCED_RECIPIENTS: {
      const hasInitialRecipients =
        action.payload.recipients?.length > 0 ||
        action.payload.groups?.length > 0 ||
        action.payload.sites?.length > 0 ||
        action.payload.savedSearches?.length > 0;
      return {
        ...state,
        composeMessageInProgress: true,
        hasEnhancedRecipientManagement: true,
        recipients: action.payload.recipients,
        groups: action.payload.groups,
        sites: action.payload.sites,
        savedSearches: action.payload.savedSearches,
        organizationIds: action.payload.organizationIds,
        templateToSend: action.payload.templateToSend,
        preserveRecipients: hasInitialRecipients && !action.payload.templateToSend,
        initialSubject: action.payload.initialSubject,
        initialBody: action.payload.initialBody,
      };
    }

    case types.END_COMPOSE_MESSAGE:
      return {
        ...state,
        composeMessageInProgress: false,
        hasEnhancedRecipientManagement: false,
        preserveRecipients: false,
        shouldFetchTemplateRecipients: false,
        recipients: [],
        groups: [],
        savedSearches: [],
        sites: [],
        optionalRecipients: [],
        optionalGroups: [],
        optionalSavedSearches: [],
        optionalSites: [],
        additionalRecipients: [],
        additionalGroups: [],
        additionalSavedSearches: [],
        additionalSites: [],
        organizationIds: null,
        scheduledMessage: undefined,
        templateToSend: undefined,
        initialSubject: undefined,
        initialBody: undefined,
      };

    case types.SET_SHOULD_FETCH_TEMPLATE_RECIPIENTS:
      return {...state, shouldFetchTemplateRecipients: action.payload};

    case types.CREATE_DRAFT_MESSAGE_REQUEST:
      return {
        ...state,
        isCreatingDraft: true,
        recipientIds: [],
        organizationIds: null,
        fromEmail: undefined,
      };

    case types.CREATE_DRAFT_MESSAGE_SUCCESS:
      return {
        ...state,
        author: action.payload.author,
        messageId: action.payload.id,
        recipientIds: action.payload.initiationContext?.personFilter?.personIds,
        initiationContext: action.payload.initiationContext,
        isCreatingDraft: false,
        organizationIds: action.payload.organizationIds,
        includeSubOrganizations: action.payload.includeSubOrganizations,
        fromEmail: action.payload.fromEmail,
        hasEnhancedRecipientManagement: action.payload.hasEnhancedRecipientManagement,
      };

    case types.CREATE_DRAFT_MESSAGE_FAILURE:
      return {
        ...state,
        isCreatingDraft: false,
      };

    case types.GET_ALSAT_MESSAGE_RECIPIENTS_REQUEST:
      return {
        ...state,
        hasEnhancedRecipientManagement: true,
        fetchingRecipients: true,
        recipients: [],
        optionalRecipients: [],
      };

    case types.GET_ALSAT_MESSAGE_RECIPIENTS_SUCCESS:
      return {
        ...state,
        recipients: action.payload.recipients,
        optionalRecipients: action.payload.optionalRecipients,
        pageable: action.payload.pageable || state.pageable,
        fetchingRecipients: false,
      };

    case types.GET_ALSAT_MESSAGE_RECIPIENTS_FAILURE:
      return {
        ...state,
        hasEnhancedRecipientManagement: false,
        fetchingRecipients: false,
      };

    case types.GET_MESSAGE_RECIPIENTS_REQUEST:
      return {
        ...state,
        fetchingRecipients: true,
        recipients: [],
        optionalRecipients: [],
      };

    case types.GET_MESSAGE_RECIPIENTS_SUCCESS:
      return {
        ...state,
        recipients: action.payload.content,
        pageable: action.payload.pageable,
        fetchingRecipients: false,
      };

    case types.SCHEDULE_MESSAGE_REQUEST:
      return {
        ...state,
        isSchedulingMessage: true,
      };

    case types.SCHEDULE_MESSAGE_SUCCESS:
    case types.SCHEDULE_MESSAGE_FAILURE:
      return {
        ...state,
        isSchedulingMessage: false,
      };

    case types.SEND_MESSAGE_REQUEST:
      return {
        ...state,
        isSendingDraft: true,
      };

    case types.SEND_MESSAGE_SUCCESS:
    case types.SEND_MESSAGE_FAILURE:
      return {
        ...state,
        isSendingDraft: false,
      };

    case types.DELETE_RECIPIENT_REQUEST:
      return {
        ...state,
        deletingRecipient: true,
      };

    case types.DELETE_RECIPIENT_SUCCESS:
      return {
        ...state,
        deletedRecipients: [...state.deletedRecipients, action.payload],
        deletingRecipient: false,
      };

    case types.DELETE_RECIPIENT_FAILURE:
      return {
        ...state,
        deletingRecipient: false,
      };

    case types.ADD_RECIPIENT_REQUEST:
      return {
        ...state,
        addingRecipient: true,
      };

    case types.ADD_RECIPIENT_SUCCESS:
      return {
        ...state,
        addedRecipients: [...state.addedRecipients, action.payload],
        addingRecipient: false,
      };
    case types.ADD_ALL_RECIPIENTS:
      return {
        ...state,
        recipients: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_GROUPS:
      return {
        ...state,
        groups: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_SAVED_SEARCHES:
      return {
        ...state,
        savedSearches: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_SITES:
      return {
        ...state,
        sites: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_OPTIONAL_RECIPIENTS:
      return {
        ...state,
        optionalRecipients: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_OPTIONAL_GROUPS:
      return {
        ...state,
        optionalGroups: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_OPTIONAL_SAVED_SEARCHES:
      return {
        ...state,
        optionalSavedSearches: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_OPTIONAL_SITES:
      return {
        ...state,
        optionalSites: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_ADDITIONAL_RECIPIENTS:
      return {
        ...state,
        additionalRecipients: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_ADDITIONAL_GROUPS:
      return {
        ...state,
        additionalGroups: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_ADDITIONAL_SAVED_SEARCHES:
      return {
        ...state,
        additionalSavedSearches: action.payload,
        addingRecipient: false,
      };

    case types.ADD_ALL_ADDITIONAL_SITES:
      return {
        ...state,
        additionalSites: action.payload,
        addingRecipient: false,
      };

    case types.ADD_RECIPIENT_FAILURE:
      return {
        ...state,
        addingRecipient: false,
      };

    case types.GET_MESSAGE_TEMPLATES_SUCCESS:
      return {
        ...state,
        messageTemplates: action.payload.messageTemplates,
      };

    case types.GET_MESSAGE_TEMPLATES_REQUEST:
    case types.GET_MESSAGE_TEMPLATES_FAILURE:
      return {
        ...state,
        messageTemplates: [],
      };

    case types.GET_SCHEDULED_MESSAGE_BY_ID_REQUEST:
      return {
        ...state,
        scheduledMessage: undefined,
        scheduledMessageLoading: true,
      };

    case types.GET_SCHEDULED_MESSAGE_BY_ID_FAILURE:
      return {
        ...state,
        scheduledMessage: undefined,
        scheduledMessageLoading: false,
      };

    case types.GET_SCHEDULED_MESSAGE_BY_ID_SUCCESS: {
      const fetchedRecipients =
        action.payload.scheduledMessage?.[MANAGED_RECIPIENTS]?.[RECIPIENT_TYPE_ID_KEYS.PERSON_IDS] || [];
      const fetchedSites = action.payload.scheduledMessage?.[MANAGED_RECIPIENTS]?.[RECIPIENT_TYPE_ID_KEYS.SITE_IDS];
      const originalSites = action.payload.scheduledMessage?.[MANAGED_RECIPIENTS]?.sites;

      const sitesData = fetchedSites?.length
        ? fetchedSites
        : (originalSites || []).map((site) => ({
            id: site.id || site.siteId, // Use the unique id from API, fallback to siteId
            siteId: site.siteId,
            includePOC: site.siteTypes?.includes('POC') || false,
            includeOccupants: site.siteTypes?.includes('OCCUPANT') || false,
          }));

      return {
        ...state,
        hasEnhancedRecipientManagement: true,
        preserveRecipients: false,
        organizationIds: action.payload.organizationIds,
        scheduledMessage: action.payload.scheduledMessage,
        scheduledMessageLoading: false,
        personIds: fetchedRecipients.map((recipient) => recipient.id || recipient),
        recipients: fetchedRecipients,
        groups: action.payload.scheduledMessage?.[MANAGED_RECIPIENTS]?.[RECIPIENT_TYPE_ID_KEYS.PERSON_GROUP_IDS] || [],
        savedSearches:
          action.payload.scheduledMessage?.[MANAGED_RECIPIENTS]?.[RECIPIENT_TYPE_ID_KEYS.SAVED_SEARCH_IDS] || [],
        sites: sitesData,
      };
    }

    case types.CLEAR_SCHEDULED_MESSAGE:
      return {
        ...state,
        scheduledMessage: undefined,
        recipients: [],
        groups: [],
        savedSearches: [],
        optionalRecipients: [],
        optionalGroups: [],
        optionalSavedSearches: [],
        optionalSites: [],
        additionalRecipients: [],
        additionalGroups: [],
        additionalSavedSearches: [],
        additionalSites: [],
        organizationIds: null,
        templateToSend: undefined,
      };

    case types.INITIATE_SENDER_RECIPIENTS_FETCH:
      return {
        ...state,
        hasEnhancedRecipientManagement: true,
        recipients: [],
        optionalRecipients: [],
        fetchingRecipients: true,
        fetchingOptionalRecipients: true,
        groups: [],
        optionalGroups: [],
        fetchingGroups: true,
        fetchingOptionalGroups: true,
        savedSearches: [],
        optionalSavedSearches: [],
        fetchingSavedSearches: true,
        fetchingOptionalSavedSearches: true,
        sites: [],
        optionalSites: [],
      };

    case types.GET_ALL_SENDER_RECIPIENTS_SUCCESS:
      return {
        ...state,
        recipients: action.payload.recipients?.content || [],
        optionalRecipients: action.payload.optionalRecipients?.content || [],
        fetchingRecipients: false,
        fetchingOptionalRecipients: false,
        groups: action.payload.groups?.content || [],
        optionalGroups: action.payload.optionalGroups?.content || [],
        fetchingGroups: false,
        fetchingOptionalGroups: false,
        savedSearches: action.payload.savedSearches?.content || [],
        optionalSavedSearches: action.payload.optionalSavedSearches?.content || [],
        fetchingOptionalSavedSearches: false,
        fetchingSavedSearches: false,
        sites: action.payload.sites?.content || [],
        optionalSites: action.payload.optionalSites?.content || [],
        pageable: action.payload.recipients?.pageable || action.payload.optionalRecipients?.pageable || state.pageable,
      };

    case types.GET_ALL_SENDER_RECIPIENTS_FAILURE:
      return {
        ...state,
        fetchingRecipients: false,
        fetchingOptionalRecipients: false,
        fetchingGroups: false,
        fetchingOptionalGroups: false,
        fetchingOptionalSavedSearches: false,
        recipients: [],
        optionalRecipients: [],
        groups: [],
        optionalGroups: [],
        savedSearches: [],
        optionalSavedSearches: [],
        sites: [],
        optionalSites: [],
      };

    case types.GET_MANAGED_RECIPIENTS_REQUEST:
      return {
        ...state,
        fetchingRecipients: true,
        additionalRecipients: [],
      };

    case types.GET_MANAGED_RECIPIENTS_SUCCESS:
      return {
        ...state,
        fetchingRecipients: false,
        additionalRecipients: action.payload,
        addedRecipients: action.payload,
      };

    case types.GET_MANAGED_RECIPIENTS_FAILURE:
      return {
        ...state,
        fetchingRecipients: false,
        additionalRecipients: [],
      };

    default: {
      return state;
    }
  }
};

export default communicatorReducer;
